import type { ReactNode } from 'react'
import { Check, CircleHelp, CircleX, Minus } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'

export function PanelSection({
  title,
  icon: Icon,
  children,
}: {
  title: string
  icon?: LucideIcon
  children: ReactNode
}) {
  return (
    <section>
      {Icon ? (
        <div className="flex items-center gap-2">
          <Icon aria-hidden="true" className="size-4 shrink-0 text-secondary" />
          <Typography variant="section-title" className="text-primary">
            {title}
          </Typography>
        </div>
      ) : (
        <Typography variant="section-title" className="text-primary">
          {title}
        </Typography>
      )}
      <div className="mt-3">{children}</div>
    </section>
  )
}

export type CriterionTone = 'met' | 'not-met' | 'missing' | 'not-applicable'

const TONE_ICON: Record<CriterionTone, LucideIcon> = {
  met: Check,
  'not-met': CircleX,
  missing: CircleHelp,
  'not-applicable': Minus,
}

const TONE_MARK: Record<CriterionTone, string> = {
  met: 'bg-emerald-50 text-emerald-700',
  'not-met': 'bg-red-50 text-destructive',
  missing: 'bg-amber-50 text-amber-700',
  'not-applicable': 'bg-primary/5 text-primary/50',
}

// Fixed size + self-start: sized by its row, the mark stretches into an oval when a name wraps.
export function CriterionMark({
  tone,
  label,
  className,
}: {
  tone: CriterionTone
  label: string
  className?: string
}) {
  const Icon = TONE_ICON[tone]

  return (
    <span
      title={label}
      className={cn(
        'flex size-4.5 shrink-0 items-center justify-center self-start rounded-full',
        TONE_MARK[tone],
        className
      )}
    >
      <Icon aria-hidden="true" className="size-3" />
      <span className="sr-only">{label}</span>
    </span>
  )
}

// Not PanelSection: that is 16px Inter; this panel's headings are 13px bold Univers.
export function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <Typography
      variant="body"
      as="h3"
      className="mb-2 text-sm font-bold text-primary"
    >
      {children}
    </Typography>
  )
}
